import { useState } from 'react';
import { useCharacterSymbols, useUpsertSymbol, type SymbolProgressDto } from '../../lib/characterProgress';
import { useSymbolCatalog } from '../../lib/reference';
import { projectSymbolCompletion, symbolProgressFraction } from '../../lib/symbols';
import { CollapsibleSection } from './CollapsibleSection';

const TYPE_LABEL: Record<string, string> = { ARCANE: 'Arcane', SACRED: 'Sacred', GRAND_SACRED: 'Grand Sacred' };

function SymbolRow({
  progress,
  characterLevel,
  dailyRate,
  levelCosts,
  onSave,
  pending,
}: {
  progress: SymbolProgressDto;
  characterLevel: number;
  dailyRate: number | null;
  levelCosts: { level: number; symbolsRequired: number; mesoCost: number | null; verified: boolean }[];
  onSave: (level: number, symbolsHeld: number) => void;
  pending: boolean;
}) {
  const unlocked = characterLevel >= progress.levelReq;
  const [heldDraft, setHeldDraft] = useState(String(progress.symbolsHeld));

  const totalToMax = levelCosts.find((c) => c.level === 0) ?? null;
  const projection = projectSymbolCompletion({
    level: progress.level,
    maxLevel: progress.maxLevel,
    symbolsHeld: progress.symbolsHeld,
    dailyRate,
    levelCosts,
    totalToMax,
  });
  const fraction = symbolProgressFraction(progress.level, progress.maxLevel);

  const commitHeld = () => {
    const parsed = Number(heldDraft);
    const value = Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : progress.symbolsHeld;
    if (value !== progress.symbolsHeld) {
      onSave(progress.level, value);
    } else {
      setHeldDraft(String(progress.symbolsHeld));
    }
  };

  return (
    <li
      className={['rounded-lg border border-border bg-bg-elevated-hover p-3', unlocked ? '' : 'opacity-50'].join(' ')}
      title={unlocked ? undefined : `Requires level ${progress.levelReq}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-text">{progress.symbolName}</p>
          <p className="truncate text-xs text-text-faint">
            {progress.region} · Lv.{progress.levelReq}+
            {!progress.verified ? (
              <span className="underline decoration-dotted decoration-warning/70 underline-offset-2"> · unverified</span>
            ) : (
              ''
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label={`Decrease ${progress.symbolName} level`}
            disabled={pending || !unlocked || progress.level <= 0}
            onClick={() => onSave(progress.level - 1, progress.symbolsHeld)}
            className="flex min-h-tap min-w-tap items-center justify-center rounded-lg text-text-muted hover:bg-bg-elevated disabled:opacity-30"
          >
            −
          </button>
          <span className="min-w-[3.5rem] text-center text-sm font-semibold tabular-nums text-text">
            {progress.level} / {progress.maxLevel}
          </span>
          <button
            type="button"
            aria-label={`Increase ${progress.symbolName} level`}
            disabled={pending || !unlocked || progress.level >= progress.maxLevel}
            onClick={() => onSave(progress.level + 1, progress.symbolsHeld)}
            className="flex min-h-tap min-w-tap items-center justify-center rounded-lg text-text-muted hover:bg-bg-elevated disabled:opacity-30"
          >
            +
          </button>
        </div>
      </div>

      <div className="mt-2 h-2 overflow-hidden rounded-full bg-bg-elevated">
        <div className="h-full rounded-full bg-accent" style={{ width: `${fraction * 100}%` }} />
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-text-faint">
          Symbols held
          <input
            type="number"
            inputMode="numeric"
            min={0}
            disabled={pending || !unlocked}
            value={heldDraft}
            onChange={(e) => setHeldDraft(e.target.value)}
            onBlur={commitHeld}
            className="min-h-tap w-20 rounded-lg border border-border-strong bg-bg-elevated px-2 text-sm text-text focus:border-accent focus:outline-none"
          />
        </label>

        {projection.available ? (
          <p className="text-xs text-text-faint">
            {projection.symbolsRemaining} symbols to next tracked level
            {projection.daysRemaining !== null ? ` · ~${projection.daysRemaining}d at current rate` : ''}
          </p>
        ) : projection.totalSymbolsToMax !== null ? (
          <p className="text-xs text-text-faint" title="No per-level cost breakdown exists in the research — total to max is shown, not an ETA.">
            Total to max: {projection.totalSymbolsToMax.toLocaleString()} symbols (cost table incomplete, no ETA)
          </p>
        ) : null}
      </div>
    </li>
  );
}

export function SymbolsSection({ characterId, characterLevel }: { characterId: number; characterLevel: number }) {
  const progressQuery = useCharacterSymbols(characterId);
  const catalogQuery = useSymbolCatalog();
  const upsert = useUpsertSymbol(characterId);

  if (progressQuery.isPending || catalogQuery.isPending) {
    return (
      <CollapsibleSection title="Symbols">
        <p className="text-sm text-text-muted">Loading symbols…</p>
      </CollapsibleSection>
    );
  }
  if (progressQuery.isError || catalogQuery.isError) {
    return (
      <CollapsibleSection title="Symbols">
        <p role="alert" className="text-sm text-danger">
          Could not load symbols.
        </p>
      </CollapsibleSection>
    );
  }

  const catalogBySlug = new Map((catalogQuery.data ?? []).map((s) => [s.slug, s]));
  const byType = new Map<string, SymbolProgressDto[]>();
  for (const progress of progressQuery.data ?? []) {
    const bucket = byType.get(progress.symbolType);
    if (bucket) {
      bucket.push(progress);
    } else {
      byType.set(progress.symbolType, [progress]);
    }
  }

  const pendingSlug = upsert.isPending ? upsert.variables?.symbolSlug : null;

  return (
    <CollapsibleSection title="Symbols" subtitle="Arcane & Sacred symbol levels, filtered by this character's level">
      {[...byType.entries()].map(([type, symbols]) => (
        <div key={type}>
          <h3 className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-text-faint">
            {TYPE_LABEL[type] ?? type}
          </h3>
          <ul className="space-y-2">
            {symbols.map((progress) => {
              const catalog = catalogBySlug.get(progress.symbolSlug);
              return (
                <SymbolRow
                  key={progress.symbolSlug}
                  progress={progress}
                  characterLevel={characterLevel}
                  dailyRate={catalog?.dailySource?.amount ?? null}
                  levelCosts={catalog?.levelCosts ?? []}
                  pending={pendingSlug === progress.symbolSlug}
                  onSave={(level, symbolsHeld) =>
                    upsert.mutate({ symbolSlug: progress.symbolSlug, level, symbolsHeld })
                  }
                />
              );
            })}
          </ul>
        </div>
      ))}
      {(progressQuery.data ?? []).length === 0 && <p className="text-sm text-text-muted">No symbols seeded.</p>}
    </CollapsibleSection>
  );
}
